import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { readSnapshot, writeSnapshot, estErreurReseau } from '../services/offline';
import { makeMatcher } from '../services/search';
import SearchField from '../components/SearchField';
import AppFormModal from '../components/AppFormModal';
import Logo from '../components/Logo';

const colors = ['#E46138','#0F56E3','#062259','#019EF7','#7F7F7F'];

const SUPPORT_URL = 'https://spektysupport.atlassian.net/servicedesk/customer/portals';


// Filet côté client. Le backend refuse désormais d'enregistrer autre chose
// qu'une URL http(s), mais les outils déjà en base n'ont jamais été validés :
// React ne filtre pas le schéma d'un href, et « javascript:… » s'exécuterait
// au premier clic d'un collaborateur.
const lienSur = (url) => {
  if (typeof url !== 'string' || url.trim() === '') return false;
  try {
    const { protocol } = new URL(url, window.location.origin);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2) : '?';

export default function PortalPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState('all');
  const [search, setSearch] = useState('');
  const [horsLigneDepuis, setHorsLigneDepuis] = useState(null);
  // Édition directe depuis le portail, réservée aux admins.
  const [appAEditer, setAppAEditer] = useState(null);
  const [tousLesPoles, setTousLesPoles] = useState([]);
  const searchRef = useRef(null);

  const chargerApps = useCallback(() => api.get('/applications')
    .then(res => {
      const liste = res.data.data || [];
      setApps(liste);
      writeSnapshot('applications', liste);
      setHorsLigneDepuis(null);
    })
    .catch(error => {
      // Sans réseau, on réaffiche la dernière liste connue plutôt qu'un portail
      // vide : c'est le seul contenu que l'application puisse encore montrer.
      const instantane = estErreurReseau(error) ? readSnapshot('applications') : null;
      if (instantane) { setApps(instantane.data); setHorsLigneDepuis(instantane.savedAt); }
      else console.error(error);
    })
    .finally(() => setLoading(false)), []);

  useEffect(() => { chargerApps(); }, [chargerApps]);

  // La liste complète des pôles n'est nécessaire qu'au formulaire d'édition, et
  // la route est réservée aux admins : on ne la demande donc que pour eux.
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/groups')
      .then(r => setTousLesPoles(r.data.data || []))
      .catch(() => setTousLesPoles([]));
  }, [isAdmin]);

  // Le réseau revient : on rafraîchit de nous-mêmes plutôt que de laisser
  // l'utilisateur devant une liste périmée jusqu'à ce qu'il pense à recharger.
  useEffect(() => {
    window.addEventListener('online', chargerApps);
    return () => window.removeEventListener('online', chargerApps);
  }, [chargerApps]);

  // « / » place le curseur dans la recherche, comme sur la plupart des portails.
  // On laisse passer la touche si l'utilisateur est déjà en train de saisir.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Les filtres partent des pôles de l'utilisateur : il doit pouvoir filtrer sur
  // ceux auxquels il appartient, même si aucun outil n'y est encore rattaché —
  // un chip à 0 dit quelque chose, une rangée vide ne dit rien. On y ajoute les
  // pôles portés par les outils visibles : pour un utilisateur standard ce sont
  // déjà les siens (getUserApplications ne renvoie que l'intersection), pour un
  // admin qui voit tout cela complète avec l'ensemble des pôles utilisés.
  const groups = [];
  const seenGroups = new Set();
  for (const g of [...(user?.groups || []), ...apps.flatMap(a => a.groups || [])]) {
    if (!seenGroups.has(g.id)) { seenGroups.add(g.id); groups.push(g); }
  }
  groups.sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name));

  // Recherche multi-termes : « suivi chantier » ne remonte que les outils qui
  // contiennent les deux mots, dans le nom, la description ou le nom d'un pôle.
  const correspond = makeMatcher(search);
  const searchResults = apps.filter(a =>
    correspond(a.name, a.description, ...(a.groups || []).map(g => g.name)));

  // Compteurs calculés sur la recherche mais pas sur le filtre actif : chaque
  // chip annonce ce qu'il afficherait si on cliquait dessus.
  const counts = new Map([['all', searchResults.length]]);
  for (const app of searchResults) {
    for (const g of app.groups || []) counts.set(g.id, (counts.get(g.id) || 0) + 1);
  }

  const filteredApps = activeGroup === 'all'
    ? searchResults
    : searchResults.filter(a => (a.groups || []).some(g => g.id === activeGroup));

  const isFiltering = search.trim() !== '' || activeGroup !== 'all';
  const resetFilters = () => { setSearch(''); setActiveGroup('all'); };

  // On navigue dans le gestionnaire de clic plutôt que d'attendre que la
  // disparition de l'utilisateur fasse rendre le <Navigate> de PrivateRoute :
  // la sortie est immédiate au lieu de coûter un cycle de rendu de plus.
  const seDeconnecter = () => { logout(); navigate('/login', { replace: true }); };

  const renderIcon = (app) => {
    if (app.iconType === 'emoji') return <span style={{ fontSize: 32 }}>{app.icon}</span>;
    if (app.icon) return <img src={app.icon} alt={app.name} style={{ width: 40, height: 40, objectFit: 'contain' }} />;
    return <span style={{ fontSize: 28, color: '#fff' }}>{getInitials(app.name)}</span>;
  };

  return (
    <div style={styles.wrapper}>
      {/* HEADER */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logo}>
            <Logo size={44} />
            <span style={styles.logoText}>Portail</span>
          </div>
          <div style={styles.headerRight}>
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" style={styles.supportBtn}>
              🛟 Support
            </a>
            {isAdmin && (
              <button onClick={() => navigate('/admin')} style={styles.adminBtn}>
                ⚙ Administration
              </button>
            )}
            <div style={styles.avatar} title={user?.email}>
              {getInitials(`${user?.firstName || ''} ${user?.lastName || ''}`)}
            </div>
            <button onClick={seDeconnecter} style={styles.logoutBtn} title="Déconnexion">↩</button>
          </div>
        </div>
      </header>

      {/* HERO BANNER */}
      <div style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Bonjour, {user?.firstName || user?.email?.split('@')[0]} 👋
          </h1>
          <p style={styles.heroSub}>Accédez à toutes vos ressources depuis un seul endroit</p>
          {user?.groups?.length > 0 && (
            <div style={styles.heroGroups}>
              <span style={styles.heroGroupsLabel}>Vos groupes :</span>
              {user.groups.map(g => (
                <span key={g.id} style={styles.heroGroupChip}>
                  {g.icon && <span style={{ marginRight: 4 }}>{g.icon}</span>}
                  {g.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main style={styles.main}>
        {/* Barre d'outils : recherche + filtres réunis dans une seule carte qui
            chevauche le bandeau (voir styles.main). Les boutons ne flottent plus
            sur le dégradé, ils appartiennent à un bloc identifiable. */}
        {!loading && apps.length > 0 && (
          <div className="portal-toolbar">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Rechercher un outil..."
              inputRef={searchRef}
              showShortcut
            />

            {groups.length > 0 && (
              <div className="portal-filters" role="group" aria-label="Filtrer par pôle">
                <FilterChip
                  label="Tous"
                  count={counts.get('all') || 0}
                  active={activeGroup === 'all'}
                  onClick={() => setActiveGroup('all')}
                />
                {groups.map(g => (
                  <FilterChip
                    key={g.id}
                    icon={g.icon}
                    label={g.name}
                    count={counts.get(g.id) || 0}
                    active={activeGroup === g.id}
                    onClick={() => setActiveGroup(g.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {horsLigneDepuis && (
          <div style={styles.horsLigne} role="status">
            <span aria-hidden="true">⚠</span>
            <span>
              Hors ligne — liste enregistrée le{' '}
              {new Date(horsLigneDepuis).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}.
              Les outils ne s'ouvriront pas tant que la connexion n'est pas revenue.
            </span>
          </div>
        )}

        {/* Grille d'applications */}
        {loading ? (
          <div className="apps-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} style={styles.skeletonCard} />
            ))}
          </div>
        ) : filteredApps.length === 0 ? (
          <div style={styles.empty}>
            <span style={{ fontSize: 48 }}>{isFiltering ? '🔍' : '📭'}</span>
            <p style={{ marginTop: 12, color: '#7F7F7F' }}>
              {isFiltering
                ? `Aucun outil ne correspond${search.trim() ? ` à « ${search.trim()} »` : ' à ce filtre'}.`
                : 'Aucune application disponible'}
            </p>
            {isFiltering && (
              <button type="button" onClick={resetFilters} style={styles.resetBtn}>
                Réinitialiser
              </button>
            )}
          </div>
        ) : (
          <div className="apps-grid" key={activeGroup}>
            {filteredApps.map((app, i) => {
              const ouvrable = lienSur(app.url);
              return (
              <div className="app-card-wrap" key={app.id}>
              <a
                href={ouvrable ? app.url : undefined}
                target={ouvrable ? '_blank' : undefined}
                rel="noopener noreferrer"
                title={ouvrable ? undefined : "Lien invalide — signalez-le à un administrateur"}
                aria-disabled={ouvrable ? undefined : true}
                className="app-card"
                // Décalage en cascade, plafonné : au-delà d'une quinzaine de
                // cartes le retard deviendrait une attente, pas un effet.
                style={{ ...styles.card, animationDelay: `${Math.min(i, 14) * 22}ms` }}
              >
                <div style={{ ...styles.cardIcon, background: app.color || colors[i % colors.length] }}>
                  {renderIcon(app)}
                </div>
                <div style={styles.cardContent}>
                  <h3 style={styles.cardTitle}>{app.name}</h3>
                  {app.description && <p style={styles.cardDesc}>{app.description}</p>}
                  {/* Un outil peut être rattaché à une dizaine de pôles. La rangée
                      tient sur une seule ligne quoi qu'il arrive : un badge qui
                      s'élide, puis un « +N » dont l'infobulle liste le reste. */}
                  {(app.groups || []).length > 0 && (
                    <div style={styles.cardBadges}>
                      <span style={styles.cardBadge} title={app.groups.map(g => g.name).join('\n')}>
                        {app.groups[0].icon && <span style={{ marginRight: 3 }}>{app.groups[0].icon}</span>}
                        {app.groups[0].name}
                      </span>
                      {app.groups.length > 1 && (
                        <span
                          style={styles.cardBadgeMore}
                          title={app.groups.slice(1).map(g => g.name).join('\n')}
                        >
                          +{app.groups.length - 1}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="app-card-arrow" style={styles.cardArrow}>{ouvrable ? '→' : '⚠'}</div>
              </a>
              {/* Hors du <a> : un bouton dans un lien est invalide, et le clic
                  déclencherait l'ouverture de l'outil. */}
              {isAdmin && (
                <button
                  type="button"
                  className="app-card-edit"
                  onClick={() => setAppAEditer(app)}
                  title={`Modifier ${app.name}`}
                  aria-label={`Modifier ${app.name}`}
                >
                  ✏
                </button>
              )}
              </div>
              );
            })}
          </div>
        )}
      </main>

      {appAEditer && (
        <AppFormModal
          app={appAEditer}
          groups={tousLesPoles}
          onClose={() => setAppAEditer(null)}
          onSaved={chargerApps}
        />
      )}
    </div>
  );
}

// Chip de filtre. L'apparence vit dans global.css (.portal-chip) et non en
// style inline : un style inline l'emporterait sur les règles :hover /
// :focus-visible, qui n'existent de toute façon pas en inline.
function FilterChip({ icon, label, count, active, onClick }) {
  const className = ['portal-chip', active ? 'is-active' : '', count === 0 ? 'is-empty' : '']
    .filter(Boolean).join(' ');
  return (
    <button type="button" onClick={onClick} className={className} aria-pressed={active} title={label}>
      {icon && <span aria-hidden="true">{icon}</span>}
      <span className="portal-chip-label">{label}</span>
      <span className="portal-chip-count">{count}</span>
    </button>
  );
}

const styles = {
  wrapper: { minHeight: '100vh', background: '#f4f6f9', display: 'flex', flexDirection: 'column' },
  header: { background: '#062259', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', position: 'sticky', top: 0, zIndex: 100 },
  headerInner: { maxWidth: 1440, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoText: { fontSize: 20, fontWeight: 700, color: '#fff' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  adminBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  supportBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: '#E46138', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  logoutBtn: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 20, cursor: 'pointer', padding: '4px 8px' },
  hero: { background: 'linear-gradient(135deg, #062259 0%, #0F56E3 100%)', padding: '48px 24px 64px' },
  heroContent: { maxWidth: 1440, margin: '0 auto' },
  heroTitle: { fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 8 },
  heroSub: { fontSize: 16, color: 'rgba(255,255,255,0.75)' },
  heroGroups: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 16 },
  heroGroupsLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  heroGroupChip: { fontSize: 12, color: '#fff', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 20, padding: '4px 12px' },
  main: { maxWidth: 1440, margin: '-32px auto 40px', padding: '0 24px', flex: 1 },
  skeletonCard: { height: 120, background: '#fff', borderRadius: 12, animation: 'pulse 1.5s infinite' },
  card: { background: '#fff', borderRadius: 12, padding: '20px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', textDecoration: 'none', color: 'inherit' },
  cardIcon: { width: 56, height: 56, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardContent: { flex: 1, minWidth: 0 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#1a1a2e', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardDesc: { fontSize: 12, color: '#7F7F7F', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardBadges: { display: 'flex', flexWrap: 'nowrap', gap: 4, marginTop: 6, minWidth: 0 },
  cardBadge: { flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: '#0F56E3', background: '#EEF3FF', borderRadius: 4, padding: '2px 6px' },
  cardBadgeMore: { flexShrink: 0, fontSize: 11, color: '#7F7F7F', background: '#f0f1f4', borderRadius: 4, padding: '2px 6px' },
  cardArrow: { fontSize: 18, flexShrink: 0 },
  horsLigne: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: '#fff8e6', border: '1px solid #f0c65a', color: '#8a6100', fontSize: 13, lineHeight: 1.4 },
  empty: { textAlign: 'center', padding: '80px 20px', color: '#7F7F7F' },
  resetBtn: { marginTop: 16, background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
